import { PATHS } from '@/router/paths';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './styles.module.scss';
import TimeExtensionModal from './TimeExtensionModal';
import { CONFIG_CONSTANTS, useConfigStore, buildMenuFromBuildingMenus, getRequestStaffHelpFromBuildingMenus } from '@/stores/configStore';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useCountdownResetListener, useGlobalCountdownReset } from '@/hooks/useGlobalCountdownReset';
import type { GNBItemType } from '@/types';
import { useHomeStore, type languageType } from '@/stores';

export type CountdownButtonProps = {
    initialSeconds?: number;
    thresholdSeconds?: number;
    disabled?: boolean;
    idleLabel?: string;
    className?: string;
    onZoomButtonsOpen?: (isOpen: boolean) => void;
    closeModal?: () => void;
};

export default function CountdownButton({
    initialSeconds = 180,
    thresholdSeconds = 30,
    disabled,
    className = '',
    onZoomButtonsOpen,
    closeModal,
}: CountdownButtonProps) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const updateConfig = useConfigStore((state) => state.updateConfig);
    const idleTimeoutSec = useConfigStore((state) => state.config?.ui?.idleTimeoutSec);
    const server = useConfigStore((state) => state.config?.server);
    const buildingMenus = useConfigStore((state) => state.config?.server?.buildingMenus);
    const lotteEvents = useConfigStore((state) => state.config?.server?.building?.lotteEvents);
    const storeLangs = useConfigStore((state) => state.config?.server?.store?.storeLangs);
    const ui = useConfigStore((state) => state.config?.ui);
    const tts = useConfigStore((state) => state.config?.tts);
    const { resetCountdown } = useGlobalCountdownReset();
    const [timeLeft, setTimeLeft] = useState<number>(idleTimeoutSec ?? initialSeconds);
    const [isRunning, setIsRunning] = useState<boolean>(false);
    const [showPrompt, setShowPrompt] = useState<boolean>(false);

    const targetMsRef = useRef<number | null>(null);
    const intervalRef = useRef<number | null>(null);
    const initialTimeRef = useRef<number>(idleTimeoutSec ?? initialSeconds);
    const formatted = timeLeft.toString();

    const setHomeMenuVisible = useHomeStore((s) => s.setHomeMenuVisible);

    const clearTimer = useCallback(() => {
        if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const stop = useCallback(() => {
        clearTimer();
        setIsRunning(false);
    }, [clearTimer]);

    const start = useCallback(() => {
        if (disabled || isRunning) return;
        const now = Date.now();
        targetMsRef.current = now + initialTimeRef.current * 1000;
        setIsRunning(true);

        intervalRef.current = window.setInterval(() => {
            if (!targetMsRef.current) return;
            const remaining = Math.max(0, Math.round((targetMsRef.current - Date.now()) / 1000));
            setTimeLeft((prev) => (prev !== remaining ? remaining : prev));
        }, 250);
    }, [disabled, isRunning]);

    const reset = useCallback(() => {
        stop();
        setTimeLeft(initialTimeRef.current);
        setShowPrompt(false);
    }, [stop]);

    const resetAndNavigateHome = useCallback(async () => {
        reset();
        const currentLanguage = (storeLangs?.find((lang) => lang.main_lang)?.code as languageType) || CONFIG_CONSTANTS.DEFAULT_LANGUAGE;
        // Update both language and menu data using buildMenuFromBuildingMenus
        const menu: GNBItemType[] = buildMenuFromBuildingMenus(buildingMenus, currentLanguage, currentLanguage, lotteEvents);

        await updateConfig({
            ui: {
                ...ui,
                language: currentLanguage,
                menu,
                requestStaffHelp: getRequestStaffHelpFromBuildingMenus(buildingMenus, currentLanguage, server),
                zoomIn: false,
                zoomClosed: true,
                contrast: false,
                volume: 3,
                screenMode: 'fullscreen',
                zoomLevel: 1,
            },
            tts: {
                ...tts,
                voice: false,
            },
        });
        onZoomButtonsOpen?.(false);
        // notify tracking that timeout caused navigate home (end session)
        TrackingService.onTimeoutNavigateHome();
        navigate(PATHS.HOME.INDEX);
        closeModal?.();
    }, [reset, storeLangs, buildingMenus, lotteEvents, updateConfig, ui, tts, onZoomButtonsOpen, navigate, closeModal]);

    const handleConfirm = useCallback(() => {
        resetAndNavigateHome();
        setHomeMenuVisible(false);
    }, [resetAndNavigateHome, setHomeMenuVisible]);

    const handleCancel = useCallback(() => {
        resetCountdown();
    }, [resetCountdown]);

    useEffect(() => {
        if (isRunning && timeLeft <= thresholdSeconds && timeLeft > 0) {
            setShowPrompt(true);
        }
    }, [timeLeft, thresholdSeconds, isRunning]);

    useEffect(() => {
        if (timeLeft === 0 && isRunning) {
            handleConfirm();
        }
    }, [timeLeft, isRunning, handleConfirm]);

    useEffect(() => clearTimer, [clearTimer]);

    // Update ref when initialSeconds prop changes
    useEffect(() => {
        initialTimeRef.current = idleTimeoutSec ?? initialSeconds;
    }, [initialSeconds, idleTimeoutSec]);

    // auto start
    useEffect(() => {
        if (!disabled && !isRunning) {
            start();
        }
    }, [disabled, isRunning, start]);

    // Listen for global countdown reset events
    useCountdownResetListener(() => {
        if (isRunning) {
            reset();
        }
    });

    // Add touch, keyboard, and scroll event listeners to reset countdown on any interaction
    useEffect(() => {
        let touchStartY = 0;
        let touchStartX = 0;
        const minSwipeDistance = 50; // Minimum distance for swipe detection

        const handleRestart = () => {
            if (isRunning && !showPrompt) {
                reset();
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (isRunning && !showPrompt) {
                const key = event.key;

                const listKey = [
                    'Tab',
                    'ArrowUp',
                    'ArrowDown',
                    'Enter',
                    'Backspace',
                    'ArrowLeft',
                    'ArrowRight',
                    'h',
                    '*',
                    '#',
                    '1',
                    '2',
                    '3',
                    '4',
                    '5',
                    '6',
                    '7',
                    '8',
                ];

                // Check for Tab, Shift+Tab, Arrow keys, Enter, and Backspace
                if (
                    listKey.includes(key)
                ) {
                    reset();
                }
            }
        };

        const handleScroll = () => {
            if (isRunning && !showPrompt) {
                reset();
            }
        };

        const handleTouchStart = (event: TouchEvent) => {
            if (isRunning && !showPrompt && event.touches.length === 1) {
                touchStartX = event.touches[0].clientX;
                touchStartY = event.touches[0].clientY;
            }
        };

        const handleTouchMove = (event: TouchEvent) => {
            if (isRunning && !showPrompt && event.touches.length === 1) {
                const touchEndX = event.touches[0].clientX;
                const touchEndY = event.touches[0].clientY;
                const deltaX = Math.abs(touchEndX - touchStartX);
                const deltaY = Math.abs(touchEndY - touchStartY);

                // Check if it's a swipe gesture (horizontal or vertical movement > minSwipeDistance)
                if (deltaX > minSwipeDistance || deltaY > minSwipeDistance) {
                    reset();
                }
            }
        };

        const handleTouchEnd = () => {
            // Reset touch start coordinates
            touchStartX = 0;
            touchStartY = 0;
        };

        document.addEventListener('click', handleRestart);
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('scroll', handleScroll, true);
        document.addEventListener('touchstart', handleTouchStart, { passive: true });
        document.addEventListener('touchmove', handleTouchMove, { passive: true });
        document.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            document.removeEventListener('click', handleRestart);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('scroll', handleScroll, true);
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isRunning, showPrompt, reset]);

    return (
        <div className={`inline-flex flex-col items-center gap-3 ${className}`}>
            <button
                tabIndex={-1}
                disabled
                aria-live="polite"
                className={[
                    'relative font-medium transition-all select-none',
                    'h-[calc(40*var(--vh-unit))] w-[calc(176*var(--vw-unit))] flex-shrink-0 rounded-[calc(36*var(--vh-unit))] border-[calc(1*var(--vh-unit))] border-[var(--text-black-03)] bg-[var(--bg-basic)]',
                ].join(' ')}
            >
                <span className={`items-center gap-2 ${styles.countdown__buttonText}`}>
                    {t('countdown.autoReset')} <span className={styles.countdown__formattedTime}>{formatted}</span> {t('countdown.seconds')}
                </span>
            </button>

            {showPrompt && <TimeExtensionModal isOpen={showPrompt} timeLeft={timeLeft} onClose={handleCancel} onConfirm={handleConfirm} />}
        </div>
    );
}
